<template>
  <div class="similar-problem-item f-w v-centerer">
    <div
      v-quick-title="$t('message.RECURRENCE_DUPLICATE_HINT')"
      class="duplicate"
      @click="isDuplicate = !isDuplicate"
    >
      <Checkbox v-model="isDuplicate" color="yellow" @click.native.stop="" />
      <div class="cb-hint">
        {{ $t('message.RECURRENCE_DUPLICATE_SHORT') }}
      </div>
    </div>
    <div
      v-quick-title="$t('message.RELATED_HINT')"
      class="related"
      @click="isRelated = !isRelated"
    >
      <Checkbox v-model="isRelated" color="green" @click.native.stop="" />
      <div class="cb-hint">
        {{ $t('message.RELATED_SHORT') }}
      </div>
    </div>
    <div class="pb-content" :style="tileStyle" @click="displaySheet">
      <div class="img-wrapper">
        <img :src="fUtilsFirstPictureData" alt="">
      </div>
      <div class="content">
        <div class="top align-top f-w">
          <EllipsisDiv :key="lWhatKey" class="name" :text="problem.lWhat" />
          <EllipsisDiv class="date" :text="dateStr" />
        </div>
        <div class="bot align-top f-w">
          <span class="state" :style="stateStyle">
            ● {{ stateTitle }}
          </span>
          <span class="similarity">
            {{ similarity }}
          </span>
        </div>
      </div>
    </div>
    <div class="score">
      {{ score }}
    </div>
    <div class="user-actions">
      <img
        class="compare"
        alt=""
        src="@/assets/img/compare-medium-gray.png"
        @click="compare"
      >
    </div>
  </div>
</template>

<script>
/**
 * Problem item displayed when the user fetches similar problems
 * in a problem sheet or when reporting a problem
 *
 * @vue-prop {Object} problem - The similar problem to display
 * @vue-prop {Object} comparedTo - The problem that `problem` is compared to
 *
 * @vue-event {} compare - User clicked the 'compare' icon to display ProblemsComparator
 * @vue-event {} displaySheet - User clicked the problem item to display the full content
 * @vue-event {Array, Object} tick - User ticked one of the options (dentical or related)
 */

import $ from 'jquery';
// Components
import Checkbox from '@/components/checkbox.vue';
import EllipsisDiv from '@/components/ellipsize.vue';
// Mixins
import mixinFileUtils from '@/mixins/file-utils.vue';
// Utils
import { calert } from '@/utils/main';
import SocketMessages from '@/utils/classes/SocketMessages';
import { PB_STATE_COLORS } from '@/utils/constants';

const simFields = [
  { field: 'typesScore', tr: 'message.TYPES' },
  { field: 'zonesScore', tr: 'message.LOCATIONS' },
  { field: 'whatScore', tr: 'message.WHAT' },
  { field: 'keywordsScore', tr: 'message.KEYWORDS' },
  { field: 'howScore', tr: 'message.HOW' },
  { field: 'howMuchScore', tr: 'message.HOW_MUCH' },
  { field: 'whenScore', tr: 'message.WHEN' },
  { field: 'whereScore', tr: 'message.WHERE' },
  { field: 'whoScore', tr: 'message.WHO' },
  { field: 'whyScore', tr: 'message.WHY' },
];

export default {
  name: 'SimilarProblemItem',
  components: {
    Checkbox,
    EllipsisDiv,
  },
  mixins: [mixinFileUtils],
  props: {
    problem: {
      type: Object,
      required: true,
    },
    comparedTo: {
      type: Object,
      required: true,
    },
    comparedRecurrenceIds: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      lWhatKey: 1,
    };
  },
  computed: {
    stateTitle() {
      return this.$t('message.PB_STATES')[this.problem.state - 1];
    },
    /** State color displayed */
    stateStyle() {
      return `color: ${PB_STATE_COLORS[this.problem.state]}`;
    },
    /** Readable date */
    dateStr() {
      return this.$moment(this.problem.created).format(this.$t('dateFormat'));
    },
    /** Style of the pb item according to links */
    tileStyle() {
      if (this.isDuplicate) {
        return { backgroundColor: '#d8ed6d' };
      } if (this.isRelated) {
        return { backgroundColor: '#a2ed6d' };
      }
      return {};
    },
    /** Similarity explanation */
    similarity() {
      const commaStr = this.$t('message.TEXT_COMMA');
      const andStr = this.$t('message.AND');
      const quoteStr = this.$t('message.QUOTE');
      const fieldsTr = simFields.filter(f => this.problem[f.field] > 0.2).map(f => f.tr);
      let fieldsStr = '';
      fieldsTr.forEach((tr, i) => {
        fieldsStr += `${quoteStr}${this.$t(tr)}${quoteStr}`;
        if (i === fieldsTr.length - 2) {
          fieldsStr += ` ${andStr} `;
        } else if (i < fieldsTr.length - 2) {
          fieldsStr += commaStr;
        }
      });
      return fieldsTr.length === 0
        ? this.$t('message.NO_SIMILARITY')
        : this.$t('message.SIMILARITIES_FIELDS', {
          fieldsList: fieldsStr,
          fieldsWord: this.$tc('message.FIELDS_SUFFIX', fieldsTr.length),
        });
    },
    /** Trust score as a percentage */
    score() {
      const perc = this.problem.similarity * 100;
      return `${perc.toFixed(perc % 1 === 0 ? 0 : 1)}%`;
    },
    isDuplicate: {
      get() {
        return this.problem.recurrenceIds.includes(this.comparedTo.id);
      },
      set(newVal) {
        if (newVal) {
          if (this.comparedRecurrenceIds.length && this.problem.recurrenceIds.length) {
            // Trying to merge 2 sets of identical problems
            calert(this.$t('message.PB_DUPLICATE_MERGE_CONFIRM'), [
              {
                title: this.$t('message.PB_DUPLICATE_MERGE'),
                action: () => {
                  this.linksUpdated(newVal, {
                    id: this.comparedTo.id,
                    recurrenceId: this.problem.id,
                  });
                },
              },
              {
                title: this.$t('message.CANCEL'),
              },
            ]);
            return;
          }
          this.problem.recurrenceIds.push(this.comparedTo.id);
        } else {
          this.problem.recurrenceIds.splice(this.problem.recurrenceIds.indexOf(this.comparedTo.id), 1);
        }
        this.linksUpdated(newVal, {
          id: this.comparedTo.id,
          recurrenceId: this.problem.id,
        });
      },
    },
    isRelated: {
      get() {
        return this.problem.linkIds.includes(this.comparedTo.id);
      },
      set(newVal) {
        if (newVal) {
          this.problem.linkIds.push(this.comparedTo.id);
        } else {
          this.problem.linkIds.splice(this.problem.linkIds.indexOf(this.comparedTo.id), 1);
        }
        this.linksUpdated(newVal, {
          id: this.comparedTo.id,
          linkId: this.problem.id,
        });
      },
    },
    fUtilsFileIds() {
      return this.problem.files || [];
    },
  },
  created() {
    $(window).on(`resizeEnd.${this.uuid}`, () => { this.lWhatKey++; });
  },
  methods: {
    /** Display the problem's sheet as an overlay */
    displaySheet() {
      this.$emit('displaySheet');
    },
    /** Display the current problem and the sheet's problem so each field can be compared */
    compare() {
      this.$emit('compare');
    },
    /** One checkbox has been ticked */
    linksUpdated(ticked, params) {
      let url = '/server/problems/links/add.php';
      if (!ticked) {
        url = '/server/problems/links/remove.php';
      }
      this.App.post(url, params, (data) => {
        if (data instanceof Object) {
          this.$emit('tick', data, this.problem);
          this.App.sendSocketMessage({
            socketCompany: 1,
            id: this.problem.id,
            msg: SocketMessages.CC_MSGS.PROBLEM_LINKS,
          });
          this.App.sendSocketMessage({
            socketCompany: 1,
            id: this.comparedTo.id,
            msg: SocketMessages.CC_MSGS.PROBLEM_LINKS,
          });
        }
      });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.similar-problem-item {
  text-align: left;
  box-sizing: border-box;
  margin-top: 5px;

  > * {
    display: inline-block;
    vertical-align: top;
  }
  .duplicate, .related {
    width: 30px;
    text-align: center;
    cursor: pointer;

    .checkbox > img {
      margin-right: 0px;
    }
  }
  .duplicate {
    margin-left: 10px;
  }
  .related {
    margin-right: 10px;
  }
  .score {
    width: 60px;
    text-align: center;
  }
  .user-actions {
    width: 40px;

    .compare {
      padding: 3px;
      width: 20px;
      height: auto;
      cursor: pointer;

      &:hover {
        background: $color-hoverbtn-light-blue;
      }
    }
  }
  .pb-content {
    width: calc(100% - 180px);
    background: #ffffff;
    padding: 5px;
    box-sizing: border-box;
    cursor: pointer;
    transition: background 0.2s ease-out;

    &:hover {
      background: $color-hoverbtn-light-blue !important;
    }

    > * {
      display: inline-block;
      vertical-align: top;
    }
    .img-wrapper {
      img {
        width: 40px;
        height: 40px;
        object-fit: cover;
        border-radius: 35px;
        display: block;
      }
    }
    .content {
      margin-left: 10px;
      width: calc(100% - 50px);

      .top {
        margin-bottom: 3px;

        > * {
          display: inline-block;
        }
      }
      .bot {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      }
      .name {
        width: calc(100% - 85px);
        font-weight: 600;
        line-height: 1.45em;
        height: 1.45em;
        margin-right: 10px;
      }
      .date {
        font-size: 0.9em;
        width: 75px;
        line-height: 1.45em;
        height: 1.45em;
      }
      .similarity {
        font-style: italic;
        font-weight: 0.9em;
        margin-left: 10px;
      }
    }
  }
}
</style>
